import { useState } from 'react';
import {
  Button,
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  DismissibleTag,
} from '@carbon/react';
import { format0 } from 'utils/number';
import { WellPickerDirect } from './well-picker-direct';
import { WellPickerNearby } from './well-picker-nearby';
import type { PickedWell, PickerCandidate, SectionVertex } from './types';
import styles from './well-picker.module.scss';

export interface WellPickerModalProps {
  /** Adding wells to the section, or putting one in another's place. */
  mode: 'add' | 'replace';
  /** In replace mode, the well standing down and the position it holds. */
  replacing?: { uwi: string; label: string; position: number };
  /** The section line, which is what the nearby sweep measures from. */
  vertices: SectionVertex[];
  /** Wells already on the section, so neither list offers one twice. */
  excludeUwis: string[];
  onClose: () => void;
  /** Fired once with everything staged: five wells is one edit, not five. */
  onCommit: (wells: PickedWell[]) => void;
}

/**
 * Choosing wells for a Well to Well section, either to add or to stand in for one
 * already on it.
 *
 * One modal serves both, because the choosing is the same act either way — the only
 * difference is what happens to the choice, and that is the footer's business. Replace
 * therefore takes exactly one well: a second choice supersedes the first rather than
 * queueing behind it.
 *
 * Two ways in, both on show. The wells near the line are the common case and get the
 * first tab; a well named outright is the case a radius cannot serve, and gets the
 * second. Selection is staged across both and committed once, so the plane is
 * recalculated a single time however many wells were chosen and wherever they came
 * from.
 *
 * @param {WellPickerModalProps} props - Component props.
 * @return {JSX.Element} The picker.
 */
export const WellPickerModal = ({
  mode,
  replacing,
  vertices,
  excludeUwis,
  onClose,
  onCommit,
}: WellPickerModalProps): JSX.Element => {
  const [staged, setStaged] = useState<PickerCandidate[]>([]);

  /**
   * Stages or unstages one well.
   *
   * @param {PickerCandidate} well - The row's well.
   * @param {boolean} checked - Whether it is now chosen.
   */
  const stage = (well: PickerCandidate, checked: boolean) => {
    setStaged((current) => {
      const without = current.filter((entry) => entry.uwi !== well.uwi);
      if (!checked) {
        return without;
      }
      return mode === 'replace' ? [well] : [...without, well];
    });
  };

  const commit = () => {
    onCommit(
      staged.map((well) => ({
        uwi: well.uwi,
        lat: well.lat,
        lon: well.lon,
        name: well.name,
      }))
    );
  };

  const stagedUwis = staged.map((well) => well.uwi);

  const commitLabel = () => {
    if (mode === 'replace') {
      return staged.length === 0
        ? 'Replace well'
        : `Replace with ${staged[0].name ?? staged[0].uwi}`;
    }
    if (staged.length === 0) {
      return 'Add wells';
    }
    return staged.length === 1
      ? 'Add 1 well'
      : `Add ${format0(staged.length)} wells`;
  };

  return (
    <ComposedModal open onClose={onClose} size="lg" className={styles.modal}>
      <ModalHeader
        title={mode === 'replace' ? 'Replace well' : 'Add wells to section'}
      />
      <ModalBody>
        {mode === 'replace' && replacing && (
          <p className={styles.replacing}>
            {`Replacing ${replacing.label} at position ${format0(
              replacing.position
            )}. The well chosen here takes that position rather than landing at
              the end of the section.`}
          </p>
        )}

        <Tabs>
          <TabList aria-label="How to find wells">
            <Tab>Near the section</Tab>
            <Tab>Search by name or UWI</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <WellPickerNearby
                vertices={vertices}
                excludeUwis={excludeUwis}
                stagedUwis={stagedUwis}
                onStage={stage}
              />
            </TabPanel>
            <TabPanel>
              <WellPickerDirect
                excludeUwis={excludeUwis}
                stagedUwis={stagedUwis}
                onStage={stage}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </ModalBody>
      <ModalFooter>
        {/* Staged wells survive a tab switch, so the count is worth showing: it is
            the only place a well chosen on the other tab is still visible. */}
        <div className={styles.stagedSummary}>
          {staged.map((well) => (
            <DismissibleTag
              key={well.uwi}
              type="blue"
              text={well.name ?? well.uwi}
              title={`Remove ${well.name ?? well.uwi}`}
              onClose={() => stage(well, false)}
            />
          ))}
        </div>
        <Button kind="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button kind="primary" disabled={staged.length === 0} onClick={commit}>
          {commitLabel()}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};
